import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  PrTable,
  PrTableColumn,
} from '@app/components/pr-table/pr-table.component';
import {
  LOAN_AMORTIZATION_PAGE_SIZE,
  LOAN_AMORTIZATION_CARD_MODE_MIN_WIDTH,
  LoanAmortizationRow,
  LoanAmortizationTotals,
  buildMonthlyAmortizationRows,
  buildTotalAmortizationRow,
  buildYearlyAmortizationRows,
  hasDiffereStep,
  sumAmortizationRows,
} from '@app/services/loan/loan-amortization.utils';
import { LoanPalier, LoanStep } from '@app/services/loan/loan-palier.model';
import { palierBoundaryMonths } from '@app/services/loan/loan-palier.utils';

type AmortizationView = 'monthly' | 'yearly';

@Component({
  selector: 'ea-loan-amortization',
  templateUrl: './ea-loan-amortization.component.html',
  styleUrls: ['./ea-loan-amortization.component.scss'],
  standalone: false,
})
export class EaLoanAmortizationComponent implements OnChanges {
  @Input() steps?: LoanStep[];
  @Input() paliers?: LoanPalier[];
  @Input() headerTitle = 'Échéancier du crédit';
  @Input() emptyMessage = 'Aucun échéancier calculé pour ce crédit.';
  @Input() loading = false;
  @Input() compact = false;

  @ViewChild('pagination')
  private pagination?: ElementRef<HTMLElement>;

  readonly viewChoices: string[][] = [
    ['monthly', 'Mensuel'],
    ['yearly', 'Annuel'],
  ];

  readonly resolveRowClass = (row: LoanAmortizationRow): string =>
    row?.rowClass ?? '';

  view: AmortizationView = 'monthly';
  currentPage = 1;
  totalPages = 0;
  tableData: PrTable = new PrTable([], []);
  hasBoundaries = false;
  hasDiffere = false;
  totals: LoanAmortizationTotals = {
    capital: 0,
    interest: 0,
    insurance: 0,
    amountMonthly: 0,
  };

  private monthlyRows: LoanAmortizationRow[] = [];
  private yearlyRows: LoanAmortizationRow[] = [];
  private monthlyColumns: PrTableColumn[] = [];
  private yearlyColumns: PrTableColumn[] = [];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private liveAnnouncer: LiveAnnouncer,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isNewSchedule(changes)) {
      this.currentPage = 1;
    }
    if (changes['steps'] || changes['paliers']) {
      this.rebuild();
    }
  }

  get hasRows(): boolean {
    return this.monthlyRows.length > 0;
  }

  get showLegend(): boolean {
    return (
      this.view === 'monthly' &&
      this.hasRows &&
      (this.hasBoundaries || this.hasDiffere)
    );
  }

  readonly cardModeMinWidth = LOAN_AMORTIZATION_CARD_MODE_MIN_WIDTH;

  readonly cardSwiperConfig = {
    slidesPerView: 1,
    slidesOffsetBefore: 0,
    slidesOffsetAfter: 0,
    breakpoints: {},
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  };

  onViewChange(choice: { id?: string }): void {
    const view: AmortizationView =
      choice?.id === 'yearly' ? 'yearly' : 'monthly';
    if (view === this.view) {
      return;
    }
    this.view = view;
    this.refreshTable();
  }

  onGoTo(page: number): void {
    const pagination = this.pagination?.nativeElement;
    const paginationTop = pagination?.getBoundingClientRect().top;

    this.currentPage = page;
    this.refreshTable();
    this.changeDetectorRef.detectChanges();

    if (pagination) {
      this.keepInPlace(pagination, paginationTop);
    }
    this.liveAnnouncer.announce(`Page ${page} sur ${this.totalPages}`);
  }

  onNext(page: number): void {
    this.onGoTo(page + 1);
  }

  onPrevious(page: number): void {
    this.onGoTo(page - 1);
  }

  private keepInPlace(element: HTMLElement, previousTop: number): void {
    const shift = element.getBoundingClientRect().top - previousTop;

    if (!shift) {
      return;
    }

    (this.scrollContainer(element) ?? window).scrollBy({
      top: shift,
      behavior: 'instant',
    });
  }

  private scrollContainer(element: HTMLElement): HTMLElement | null {
    for (
      let parent = element.parentElement;
      parent;
      parent = parent.parentElement
    ) {
      const { overflowY } = getComputedStyle(parent);
      const scrollable = overflowY === 'auto' || overflowY === 'scroll';

      if (scrollable && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
    }

    return null;
  }

  private isNewSchedule(changes: SimpleChanges): boolean {
    const stepsChange = changes['steps'];
    return !!stepsChange && !stepsChange.firstChange && !this.compact;
  }

  private rebuild(): void {
    this.monthlyRows = buildMonthlyAmortizationRows(this.steps, this.paliers);
    this.yearlyRows = buildYearlyAmortizationRows(this.steps);

    const showPalierColumn = palierBoundaryMonths(this.paliers).size > 0;

    this.totals = sumAmortizationRows(this.monthlyRows);
    this.hasBoundaries = showPalierColumn;
    this.hasDiffere = hasDiffereStep(this.steps);
    this.monthlyColumns = this.buildColumns('monthly', showPalierColumn);
    this.yearlyColumns = this.buildColumns('yearly', false);
    this.totalPages = Math.ceil(
      this.monthlyRows.length / LOAN_AMORTIZATION_PAGE_SIZE,
    );
    this.currentPage = Math.min(this.currentPage, Math.max(this.totalPages, 1));
    this.refreshTable();
  }

  private refreshTable(): void {
    const isMonthly = this.view === 'monthly';
    const rows = isMonthly ? this.currentPageRows() : this.yearlyRows;
    this.tableData = new PrTable(
      rows.length ? [...rows, buildTotalAmortizationRow(this.totals)] : rows,
      isMonthly ? this.monthlyColumns : this.yearlyColumns,
    );
  }

  private currentPageRows(): LoanAmortizationRow[] {
    const start = (this.currentPage - 1) * LOAN_AMORTIZATION_PAGE_SIZE;
    return this.monthlyRows.slice(start, start + LOAN_AMORTIZATION_PAGE_SIZE);
  }

  private buildColumns(
    view: AmortizationView,
    showPalierColumn: boolean,
  ): PrTableColumn[] {
    const isMonthly = view === 'monthly';
    const columns: PrTableColumn[] = [
      new PrTableColumn(isMonthly ? 'Échéance' : 'Année', 'echeance'),
    ];

    if (isMonthly && showPalierColumn) {
      columns.push(new PrTableColumn('Palier', 'palier'));
    }

    if (!this.compact) {
      columns.push(
        new PrTableColumn('Capital', 'capital', 'currency'),
        new PrTableColumn('Intérêts', 'interest', 'currency'),
        new PrTableColumn('Assurance', 'insurance', 'currency'),
      );
    }

    columns.push(
      new PrTableColumn(
        isMonthly ? 'Mensualité' : 'Annualité',
        'amountMonthly',
        'currency',
      ),
      new PrTableColumn('Restant dû', 'restant', 'currency'),
    );

    return columns;
  }
}
